import React, { useCallback, useEffect, useMemo } from 'react';
import { Badge, Card, Space } from 'antd';
import { useDispatch, useSelector } from 'umi';

import TaskDesignRow from '@/components/Tasks/TaskDesignRow';
import * as LeadTaskSelectors from '@/selectors/leadTaskSelectors';
import { LeadTaskModel } from '@/typings/models/LeadTask';
import { CheckAccess } from '@/utils/access';
import FEATURE_FLAGS from '@/constants/featureFlags';
import { F } from '@/utils/intl';

import rowStyles from '@/components/Tasks/TaskDesignRow.less';

// Open tasks first, then done/retired ones, each group in due order
const sortTasks = (tasks: LeadTaskModel[]) => {
  const isClosed = (task: LeadTaskModel) => Boolean(task.is_completed || task.retired_at);
  return [...tasks].sort((a, b) =>
    Number(isClosed(a)) - Number(isClosed(b)) || String(a.due_date || '').localeCompare(String(b.due_date || '')),
  );
};

interface AppointmentTasksProps {
  appointmentId?: string;
}

/**
 * Tasks linked to this appointment, rendered with the task list's rows
 * (actions, done, drawer). No box at all when there are none.
 */
const AppointmentTasks: React.FC<AppointmentTasksProps> = ({ appointmentId }) => {
  const dispatch = useDispatch();
  const tasks = useSelector((state: DefaultRootState) => LeadTaskSelectors.selectAppointmentTasks(state, appointmentId));

  const refresh = useCallback(() => {
    if (appointmentId) dispatch({ type: 'leadTasks/fetchAppointmentTasks', payload: { appointmentId } });
  }, [appointmentId, dispatch]);

  useEffect(refresh, [refresh]);

  const sorted = useMemo(() => sortTasks(tasks || []), [tasks]);
  const openCount = sorted.filter((task) => !task.is_completed && !task.retired_at).length;

  if (!appointmentId || !sorted.length) return null;

  return (
    <CheckAccess access={null} feature={FEATURE_FLAGS.LEAD_MANAGEMENT}>
      <Card
        size="small"
        title={(
          <Space size={8}>
            <F id="entity.task.plural" defaultMessage="Tasks" />
            {openCount > 0 && <Badge count={openCount} color="blue" size="small" />}
          </Space>
        )}
      >
        <div className={rowStyles.compactList}>
          {sorted.map((task) => (
            <TaskDesignRow key={task.id} task={task} request={refresh} hideProject />
          ))}
        </div>
      </Card>
    </CheckAccess>
  );
};

export default React.memo(AppointmentTasks);
